import { cn } from "../../lib/utils";
import type { Affix } from "@lattice-php/core";
import { nodeIdentity } from "@lattice-php/core/test-id";
import type { RendererComponent } from "@lattice-php/core/types";
import { IconRenderer } from "../../icons";
import { TextLink } from "../../primitives/text-link";
import { actionMenuItemClassName, useActionMenu } from "../../action-menu-context";
import { ActionTrigger, type TriggerState, useClickBehavior } from "../../click-behavior";

const textLinkClassName =
  "text-lt-fg underline decoration-lt-border underline-offset-4 transition-colors duration-300 ease-out hover:decoration-current! dark:decoration-lt-border";

function LinkAffix({ affix, className }: { affix: Affix; className?: string }) {
  if (affix.icon) {
    return <IconRenderer className={cn("size-lt-icon-md shrink-0", className)} icon={affix.icon} />;
  }

  return <span className={cn("shrink-0 text-sm text-lt-muted-fg", className)}>{affix.text}</span>;
}

function LinkContent({
  icon,
  isMenuItem,
  label,
  prefix,
  suffix,
}: {
  icon?: string | null;
  isMenuItem: boolean;
  label: string;
  prefix?: Affix | null;
  suffix?: Affix | null;
}) {
  if (icon) {
    return <IconRenderer className="size-lt-icon-md shrink-0" icon={icon} />;
  }

  if (isMenuItem) {
    return (
      <>
        {prefix ? <LinkAffix affix={prefix} /> : null}
        <span>{label}</span>
        {suffix ? <LinkAffix affix={suffix} className="ml-auto" /> : null}
      </>
    );
  }

  if (prefix || suffix) {
    return (
      <span className="inline-flex items-center gap-1.5 align-baseline">
        {prefix ? <LinkAffix affix={prefix} /> : null}
        <span>{label}</span>
        {suffix ? <LinkAffix affix={suffix} /> : null}
      </span>
    );
  }

  return label;
}

function labelWithTextAffixes(label: string, prefix?: Affix | null, suffix?: Affix | null) {
  if (!prefix?.text && !suffix?.text) {
    return undefined;
  }

  return [prefix?.text, label, suffix?.text]
    .filter((part): part is string => Boolean(part))
    .join(" ");
}

export const LinkAdapter: RendererComponent<"link"> = ({ node }) => {
  const isMenuItem = useActionMenu();
  const { class: nodeClass, icon, label: rawLabel, prefix, suffix, unstyled } = node.props;
  const label = rawLabel ?? "";
  const behavior = useClickBehavior(node.props);
  const className = isMenuItem ? actionMenuItemClassName : undefined;
  const testId = nodeIdentity(node);
  const content = (
    <LinkContent
      icon={icon}
      isMenuItem={isMenuItem}
      label={label}
      prefix={prefix}
      suffix={suffix}
    />
  );
  const ariaLabel = icon ? label : labelWithTextAffixes(label, prefix, suffix);

  const triggerButton = ({ onClick, processing }: TriggerState) => (
    <button
      aria-label={ariaLabel}
      className={cn(unstyled ? undefined : textLinkClassName, className, nodeClass)}
      data-test={testId}
      disabled={processing}
      onClick={onClick}
      type="button"
    >
      {content}
    </button>
  );

  if (behavior.kind === "action") {
    return <ActionTrigger action={behavior.action}>{triggerButton}</ActionTrigger>;
  }

  if (behavior.kind === "effects" || behavior.kind === "modal") {
    return triggerButton({ onClick: behavior.onClick, processing: false });
  }

  return (
    <TextLink
      aria-label={ariaLabel}
      className={cn(className, nodeClass)}
      unstyled={unstyled ?? false}
      data-test={testId}
      href={behavior.kind === "navigate" ? behavior.href : "#"}
      method={behavior.kind === "navigate" ? behavior.method : "get"}
      tabIndex={node.props.tabIndex ?? undefined}
    >
      {content}
    </TextLink>
  );
};
